import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessagesSquare, Paperclip, Clock } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { EmptyState } from "@/components/Field";
import { ChatBox } from "@/components/ChatBox";
import { AutoReplyDialog } from "@/components/AutoReplyDialog";

const ThreadItem = ({ t, active, onClick }) => (
  <button onClick={onClick} data-testid={`thread-${t.patient_id}`}
    className={`w-full rounded-2xl border p-3 text-left transition-colors ${active ? "border-[#3d655b] bg-[#edf2ef]" : "border-[#e2e8f0] bg-white hover:bg-slate-50"}`}>
    <div className="flex items-center justify-between gap-2">
      <span className={`truncate text-sm ${t.unread ? "font-extrabold" : "font-semibold"}`}>{t.patient_name}</span>
      {t.unread > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-[#3d655b] px-1 text-[10px] font-bold text-white" data-testid={`thread-unread-${t.patient_id}`}>{t.unread}</span>}
    </div>
    <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-500">
      {t.last.sender === "admin" && <span>Tu:</span>}
      {t.last.attachment && <Paperclip className="h-3 w-3 shrink-0" />}
      <span className="truncate">{t.last.text || t.last.attachment?.filename}</span>
    </div>
    <div className="mt-1 text-[10px] text-slate-400">{fmtDateTime(t.last.created_at)}</div>
  </button>
);

export default function Messages() {
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [autoOpen, setAutoOpen] = useState(false);
  const sel = params.get("paziente") || "";
  const load = useCallback(() => api.get("/messages/threads").then((r) => setThreads(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const current = threads.find((t) => t.patient_id === sel);
  return (
    <div className="fade-up space-y-6" data-testid="messages-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-xs text-[#3d655b]">Chat con i pazienti</div>
          <h1 className="page-title mt-1">Messaggi</h1>
        </div>
        <button className="btn-outline" onClick={() => setAutoOpen(true)} data-testid="auto-reply-open"><Clock className="h-4 w-4" /> Risposta automatica</button>
        <AutoReplyDialog open={autoOpen} onOpenChange={setAutoOpen} />
      </div>
      {!threads.length && !sel ? (
        <EmptyState icon={MessagesSquare} title="Nessuna conversazione" text="Puoi scrivere a un paziente dalla scheda Messaggi nella sua scheda." testid="messages-empty" />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
          <div className="space-y-2" data-testid="threads-list">
            {threads.map((t) => <ThreadItem key={t.patient_id} t={t} active={t.patient_id === sel} onClick={() => setParams({ paziente: t.patient_id })} />)}
          </div>
          <div>
            {sel ? (
              <div className="space-y-3">
                <Link to={`/pazienti/${sel}`} className="text-sm font-bold text-[#3d655b] hover:underline" data-testid="messages-patient-link">{current?.patient_name || "Apri scheda paziente"}</Link>
                <ChatBox key={sel} url={`/patients/${sel}/messages`} me="admin" prefix="admin-chat" onChange={load} height="h-[520px]" />
              </div>
            ) : <EmptyState icon={MessagesSquare} title="Seleziona una conversazione" testid="messages-select" />}
          </div>
        </div>
      )}
    </div>
  );
}
